import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { panelBackground } from '@/lib/containerStyles';
import { cn } from '@/lib/utils';

interface FilterPanelShellProps {
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  clearLabel?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Shared chrome for desktop filter trays (Inventory, Market, …).
 * - Same scroll container + panel background as Inventory.
 * - Animated red "Clear Filters" footer that slides up when activeFilters > 0
 *   and slides down (with a small delay) when cleared.
 */
export function FilterPanelShell({
  hasActiveFilters,
  onClearFilters,
  clearLabel = 'Clear Filters',
  className,
  children,
}: FilterPanelShellProps) {
  const footerContentRef = useRef<HTMLDivElement>(null);
  const footerHeightRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (footerContentRef.current) {
        setFooterHeight(footerContentRef.current.scrollHeight);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    const footer = footerHeightRef.current;
    if (!footer) return;

    if (hasActiveFilters) {
      footer.style.height = '0px';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          footer.style.height = `${footerHeight}px`;
        });
      });
    } else {
      footer.style.height = `${footer.scrollHeight}px`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          footer.style.height = '0px';
        });
      });
    }
  }, [hasActiveFilters, footerHeight]);

  return (
    <div
      className={cn(
        'flex flex-col w-full h-full relative z-[30] border-r border-white/[0.08]',
        panelBackground,
        className,
      )}
    >
      <ScrollArea className="flex-1 min-h-0 max-h-full pr-1">
        {children}
      </ScrollArea>

      <div
        ref={footerHeightRef}
        className={cn(
          'border-t border-white/[0.08] overflow-hidden',
          !hasActiveFilters && 'pointer-events-none',
        )}
        style={{
          transitionProperty: 'height',
          transitionDuration: '500ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          transitionDelay: '300ms',
        }}
      >
        <div ref={footerContentRef} className="p-4 pt-3">
          <Button
            variant="outline"
            size="default"
            onClick={onClearFilters}
            className="w-full relative flex items-center justify-center bg-destructive/20 border-destructive/40 text-foreground hover:bg-destructive/25 hover:text-foreground hover:border-destructive/50 transition-colors duration-200 ease-out focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <X className="w-5 h-5 absolute left-3" />
            <span className="font-medium">{clearLabel}</span>
          </Button>
        </div>

      </div>
    </div>
  );
}
